// ============================================
// OTP VERIFICATION COMPONENT (Mobile Friendly)
// Deploy to: /src/components/OTPVerification.js
// ============================================

import React, { useState, useEffect, useRef } from 'react';
import { 
  Mail, ArrowLeft, Loader, CheckCircle, 
  AlertCircle, RefreshCw, Shield
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

const OTPVerification = ({ 
  userId, 
  userEmail,
  onSuccess, 
  onCancel 
}) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [destination, setDestination] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 480);
  
  const inputRefs = useRef([]);

  // Check for mobile on resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 480);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Send OTP on mount
  useEffect(() => {
    sendOTP();
  }, []);

  // Cooldown timer
  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  const sendOTP = async () => {
    setSending(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/otp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId })
      });
      
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send code');
      }

      setDestination(data.destination);
      setCooldown(60);
      
      // Focus first input
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleOtpChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when complete
    if (value && index === 5 && newOtp.every(d => d !== '')) {
      handleVerify(newOtp.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    // Handle paste
    if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      navigator.clipboard.readText().then(text => {
        const digits = text.replace(/\D/g, '').slice(0, 6).split('');
        const newOtp = [...otp];
        digits.forEach((d, i) => { newOtp[i] = d; });
        setOtp(newOtp);
        if (digits.length === 6) {
          handleVerify(digits.join(''));
        } else {
          inputRefs.current[digits.length]?.focus();
        }
      });
    }
  };

  const handleVerify = async (otpCode) => {
    const code = otpCode || otp.join('');
    
    if (code.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, otp: code })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Verification failed');
      }

      setSuccess('Verified successfully!');
      
      // Call success callback with token and user data
      setTimeout(() => {
        onSuccess?.(data);
      }, 500);

    } catch (err) {
      setError(err.message);
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    
    setSending(true);
    setError('');
    setOtp(['', '', '', '', '', '']);

    try {
      const response = await fetch(`${API_URL}/otp/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to resend code');
      }

      setCooldown(60);
      inputRefs.current[0]?.focus();
      
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  // Responsive styles
  const styles = {
    container: {
      padding: isMobile ? '0.5rem' : '1.5rem',
      maxWidth: '420px',
      width: '100%',
      margin: '0 auto',
      boxSizing: 'border-box'
    },
    iconContainer: {
      width: isMobile ? '40px' : '60px',
      height: isMobile ? '40px' : '60px',
      background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 0.75rem'
    },
    title: {
      margin: '0 0 0.25rem',
      fontSize: isMobile ? '1.1rem' : '1.25rem',
      color: 'white'
    },
    subtitle: {
      margin: 0,
      color: '#94a3b8',
      fontSize: isMobile ? '0.8rem' : '0.875rem'
    },
    email: {
      margin: '0.25rem 0 0',
      color: '#e2e8f0',
      fontWeight: '600',
      fontSize: isMobile ? '0.85rem' : '1rem',
      wordBreak: 'break-all'
    },
    otpContainer: {
      display: 'flex',
      justifyContent: 'center',
      gap: isMobile ? '0.35rem' : '0.5rem',
      marginBottom: isMobile ? '1rem' : '1.25rem',
      width: '100%',
      maxWidth: '320px',
      margin: '0 auto 1.25rem'
    },
    otpInput: {
      width: isMobile ? '38px' : '44px',
      height: isMobile ? '44px' : '52px',
      textAlign: 'center',
      fontSize: isMobile ? '1.25rem' : '1.5rem',
      fontWeight: 'bold',
      background: 'rgba(30, 41, 59, 0.8)',
      border: '2px solid #475569',
      borderRadius: '0.5rem',
      color: 'white',
      outline: 'none',
      transition: 'all 0.2s',
      boxSizing: 'border-box',
      flexShrink: 0
    },
    button: {
      width: '100%',
      padding: isMobile ? '0.75rem' : '0.875rem',
      background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
      border: 'none',
      borderRadius: '0.5rem',
      color: 'white',
      fontSize: isMobile ? '0.9rem' : '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      marginBottom: '1rem'
    },
    backButton: {
      width: '100%',
      padding: isMobile ? '0.65rem' : '0.75rem',
      background: 'transparent',
      border: '1px solid #475569',
      borderRadius: '0.5rem',
      color: '#94a3b8',
      fontSize: isMobile ? '0.85rem' : '0.9rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem'
    }
  };

  // Loading state while sending initial OTP
  if (sending && !destination) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <Loader size={36} color="#60a5fa" style={{ animation: 'spin 1s linear infinite' }} />
        <p style={{ marginTop: '1rem', color: '#94a3b8', fontSize: isMobile ? '0.85rem' : '1rem' }}>
          Sending verification code...
        </p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
        <div style={styles.iconContainer}>
          <Mail size={isMobile ? 24 : 32} color="white" />
        </div>
        
        <h3 style={styles.title}>Check Your Email</h3>
        <p style={styles.subtitle}>We sent a 6-digit code to</p>
        <p style={styles.email}>{destination || userEmail}</p>
      </div>

      {/* Success Message */}
      {success && (
        <div style={{
          background: 'rgba(34, 197, 94, 0.1)',
          border: '1px solid rgba(34, 197, 94, 0.3)',
          borderRadius: '0.5rem',
          padding: isMobile ? '0.75rem' : '1rem',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          justifyContent: 'center'
        }}>
          <CheckCircle size={18} color="#22c55e" />
          <span style={{ color: '#86efac', fontSize: isMobile ? '0.85rem' : '1rem' }}>{success}</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '0.5rem',
          padding: isMobile ? '0.6rem' : '0.75rem',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <AlertCircle size={16} color="#f87171" />
          <span style={{ color: '#fca5a5', fontSize: isMobile ? '0.8rem' : '0.875rem' }}>{error}</span>
        </div>
      )}

      {/* OTP Input */}
      <div style={styles.otpContainer}>
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={el => inputRefs.current[index] = el}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={1}
            value={digit}
            onChange={(e) => handleOtpChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            disabled={loading || success}
            style={{
              ...styles.otpInput,
              borderColor: digit ? '#3b82f6' : '#475569'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = digit ? '#3b82f6' : '#475569'}
          />
        ))}
      </div>

      {/* Verify Button */}
      <button
        onClick={() => handleVerify()}
        disabled={loading || otp.some(d => !d) || success}
        style={{
          ...styles.button,
          background: loading || otp.some(d => !d) 
            ? '#475569' 
            : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
          cursor: loading || otp.some(d => !d) ? 'not-allowed' : 'pointer'
        }}
      >
        {loading ? (
          <>
            <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
            Verifying...
          </>
        ) : (
          <>
            <Shield size={18} />
            Verify & Sign In
          </>
        )}
      </button>

      {/* Resend */}
      <div style={{ textAlign: 'center', marginBottom: '0.75rem' }}>
        <p style={{ margin: '0 0 0.35rem', color: '#94a3b8', fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
          Didn't receive the code?
        </p>
        <button
          onClick={handleResend}
          disabled={cooldown > 0 || sending}
          style={{
            background: 'transparent',
            border: 'none',
            color: cooldown > 0 ? '#64748b' : '#60a5fa',
            cursor: cooldown > 0 ? 'not-allowed' : 'pointer',
            fontSize: isMobile ? '0.8rem' : '0.875rem',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.25rem'
          }}
        >
          <RefreshCw size={14} />
          {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend Code'}
        </button>
      </div>

      {/* Back Button */}
      <button onClick={onCancel} style={styles.backButton}>
        <ArrowLeft size={16} />
        Back to Login
      </button>

      {/* Tip */}
      <p style={{ 
        margin: '0.75rem 0 0', 
        fontSize: isMobile ? '0.7rem' : '0.75rem', 
        color: '#64748b', 
        textAlign: 'center' 
      }}>
        💡 Check your spam folder if you don't see the email
      </p>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default OTPVerification;